import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { App as AntApp, Input, Segmented, Switch } from 'antd';
import AppBar from '../components/AppBar';
import SheetSelect from '../components/SheetSelect';
import { AsyncButton, Chip, MoneyInput } from '../components/kit';
import { AutosaveStatus, RestoredDraftBanner } from '../components/AutosaveStatus';
import usePagedOptions from '../hooks/usePagedOptions';
import useFormAutosave from '../hooks/useFormAutosave';
import { useAuth } from '../context/AuthContext';
import { useUnsavedChanges } from '../context/UnsavedChangesContext';
import * as listingsApi from '../api/listings';
import * as contactsApi from '../api/contacts';
import { checkUpload, partitionUploads, UPLOAD_RULES } from '../lib/uploadLimits';
import { compressAll } from '../lib/compressImage';
import {
  CATEGORY_OPTIONS, DEFAULT_CATEGORY, canonicalType, categoryOfType, typeOptions,
  showsField, clearHiddenFields, landAreaWords,
} from '../lib/propertyTypes';

// Create (/listings/new) and edit (/listings/:id/edit) share this form. Agents author
// SUBSALE / RENT listings only; approval to ACTIVE and PUBLIC visibility stay with HQ.
// Option sets kept in lockstep with the admin ListingFormPage so a listing created in
// the app reads/edits identically in the admin portal (same stored values + feature keys).
// Property types, and the Residential / Commercial / Industrial / Land tabs that
// narrow them, come from lib/propertyTypes — the same vocabulary as the admin form.
const TENURES = ['Freehold', 'Leasehold'].map((v) => ({ value: v, label: v }));
// Owner picker page size — the sheet scrolls the next page in.
const CONTACT_PAGE = 20;
const FURNISH = ['Unfurnished', 'Partial Furnished', 'Fully Furnished'].map((v) => ({ value: v, label: v }));
const FACING = ['North', 'North East', 'East', 'South East', 'South', 'South West', 'West', 'North West']
  .map((v) => ({ value: v, label: v }));
const STATES = [
  'Johor', 'Kedah', 'Kelantan', 'Melaka', 'Negeri Sembilan', 'Pahang', 'Perak', 'Perlis',
  'Pulau Pinang', 'Sabah', 'Sarawak', 'Selangor', 'Terengganu',
  { value: 'Kuala Lumpur', label: 'W.P. Kuala Lumpur' },
  { value: 'Putrajaya', label: 'W.P. Putrajaya' },
  { value: 'Labuan', label: 'W.P. Labuan' },
].map((v) => (typeof v === 'string' ? { value: v, label: v } : v));
const PRESET_FEATURES = [
  { key: 'swimming_pool', label: 'Swimming Pool' }, { key: 'infinity_pool', label: 'Infinity Pool' },
  { key: 'private_pool', label: 'Private Pool' }, { key: 'gym', label: 'Gym / Fitness' },
  { key: 'sky_gym', label: 'Sky Gym' }, { key: 'garden', label: 'Garden' },
  { key: 'landscaped_garden', label: 'Landscaped Garden' }, { key: 'private_garden', label: 'Private Garden' },
  { key: 'bbq_area', label: 'BBQ Area' }, { key: 'jogging_track', label: 'Jogging Track' },
  { key: 'playground', label: 'Playground' }, { key: 'tennis_court', label: 'Tennis Court' },
  { key: 'sauna', label: 'Sauna / Steam' }, { key: 'sky_lounge', label: 'Sky Lounge' },
  { key: 'co_working_space', label: 'Co-Working Space' }, { key: 'concierge', label: 'Concierge' },
  { key: 'private_lift_lobby', label: 'Private Lift Lobby' }, { key: 'smart_home', label: 'Smart Home' },
  { key: 'renovated', label: 'Renovated' }, { key: 'maid_room', label: 'Maid Room' },
  { key: 'car_porch', label: 'Car Porch' }, { key: 'covered_car_porch', label: 'Covered Car Porch' },
  { key: 'covered_parking', label: 'Covered Parking' }, { key: 'balcony', label: 'Balcony' },
  { key: 'rooftop_terrace', label: 'Rooftop Terrace' }, { key: 'courtyard', label: 'Courtyard' },
  { key: 'auto_gate', label: 'Auto Gate' }, { key: 'gated_guarded', label: 'Gated & Guarded' },
  { key: '24hr_security', label: '24-Hour Security' }, { key: 'near_school', label: 'Near School' },
  { key: 'near_mall', label: 'Near Mall' }, { key: 'near_lrt_mrt', label: 'Near LRT / MRT' },
  { key: 'pet_friendly', label: 'Pet Friendly' }, { key: 'commercial_use', label: 'Commercial Use' },
  { key: 'heritage_building', label: 'Heritage Building' }, { key: 'mini_market', label: 'Mini Market' },
  { key: 'laundry_room', label: 'Laundry Room' }, { key: 'home_theatre', label: 'Home Theatre' },
  { key: 'wine_cellar', label: 'Wine Cellar' }, { key: 'street_frontage', label: 'Street Frontage' },
];
const PRESET_KEYS = new Set(PRESET_FEATURES.map((ft) => ft.key));
// Same slug transform as the admin form so custom feature keys match across both apps.
const slugFeature = (s) => s.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
// Counts and areas are whole numbers — the server rejects 3.5 bedrooms, so the
// keypad should never have let it be typed.
const digitsOnly = (s) => String(s ?? '').replace(/\D/g, '');
const groupInt = (s) => String(s ?? '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');

export default function ListingFormPage() {
  const nav = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { message, modal } = AntApp.useApp();
  const editing = !!id;
  // "Convert to listing" from an APPROVED ALS/ALR (mirrors the admin portal): arrive
  // with the letter in router state and open pre-linked to it.
  const fromAuthForm = !editing ? location.state?.fromAuthForm || null : null;
  const { agent } = useAuth() || {};

  const [loaded, setLoaded] = useState(!editing);
  // Loaded on edit: the status decides whether autosave may write to the server
  // (a DRAFT only — a published listing's half-typed edit must never go live),
  // and updated_at whether a device copy is older than the server's.
  const [listingStatus, setListingStatus] = useState(null);
  const [listingRef, setListingRef] = useState('');
  const [serverUpdatedAt, setServerUpdatedAt] = useState(null);
  const [f, setF] = useState({
    listing_type: 'SUBSALE', property_title: '', property_address: '', state: '', area: '', postcode: '',
    property_type: undefined, property_tenure: undefined, asking_price_rm: '', price_negotiable: true,
    bedrooms: '', bathrooms: '', car_parks: '', built_up_sqft: '', land_area_sqft: '',
    furnish: undefined, floor_level: '', facing: undefined, description: '',
    owner_name: '', owner_phone: '', owner_email: '', features: {},
    authorization_form_id: undefined,
  });
  // Every user edit goes through here (or calls noteChange itself), so it is also
  // where autosave hears of it. Loads and the from-letter prefill use setF.
  const set = (patch) => {
    setF((s) => ({ ...s, ...patch }));
    autosave.noteChange();
  };
  // Residential / Commercial / Industrial / Land. Not stored — derived from the
  // saved property_type on load. It narrows the type picker and decides which
  // details the card asks for; whatever it hides goes up as null on save.
  const [propertyCategory, setPropertyCategory] = useState(DEFAULT_CATEGORY);
  const onCategory = (key) => {
    setPropertyCategory(key);
    autosave.noteChange();
    // Keep the type only if it belongs to the new tab — never "Condominium" under Land.
    if (categoryOfType(f.property_type) !== key) set({ property_type: undefined });
  };

  // Linked ALS/ALR. While a form is attached, the four values it carries are owned
  // by the signed document, not the listing — they render disabled and are edited
  // on the form itself. Clearing the link hands them back.
  const [authForms, setAuthForms] = useState([]);
  const [linkedForm, setLinkedForm] = useState(null);
  const locked = !!linkedForm;
  const lockNote = linkedForm ? `From ${linkedForm.form_ref}` : undefined;

  // Photos: on edit these are the listing's real uploaded images; on create they are
  // staged locally (with object-URL previews) and pushed after the listing is created.
  const fileRef = useRef(null);
  const [images, setImages] = useState([]);         // edit-mode: [{ id, image_url, is_cover }]
  const [staged, setStaged] = useState([]);         // create-mode: [{ key, file, url }]
  const [coverKey, setCoverKey] = useState(null);   // create-mode: which staged photo is cover
  const [imgBusy, setImgBusy] = useState(false);
  // { label, percent } while photos are being prepared or uploaded; null when idle.
  const [progress, setProgress] = useState(null);
  // Owner-from-contacts picker (reuses the shared contacts lookup typeahead).
  const [pickingOwner, setPickingOwner] = useState(false);
  const stagedRef = useRef([]);
  stagedRef.current = staged;
  useEffect(() => () => stagedRef.current.forEach((s) => URL.revokeObjectURL(s.url)), []);

  useEffect(() => {
    if (!editing) return;
    listingsApi.getListingsById(id)
      .then((r) => {
        const d = r?.data ?? r;
        setListingStatus(d.status || null);
        setListingRef(d.listing_ref || '');
        setServerUpdatedAt(d.updated_at || null);
        setF({
          listing_type: d.listing_type === 'RENT' ? 'RENT' : 'SUBSALE',
          property_title: d.property_title || '', property_address: d.property_address || '',
          state: d.state || '', area: d.area || '', postcode: d.postcode || '',
          property_type: canonicalType(d.property_type) || undefined, property_tenure: d.property_tenure || undefined,
          asking_price_rm: d.asking_price_rm != null ? String(d.asking_price_rm) : '',
          price_negotiable: d.price_negotiable !== false,
          bedrooms: d.bedrooms != null ? String(d.bedrooms) : '', bathrooms: d.bathrooms != null ? String(d.bathrooms) : '',
          car_parks: d.car_parks != null ? String(d.car_parks) : '', built_up_sqft: d.built_up_sqft != null ? String(d.built_up_sqft) : '',
          land_area_sqft: d.land_area_sqft != null ? String(d.land_area_sqft) : '',
          furnish: d.furnish || undefined, floor_level: d.floor_level || '', facing: d.facing || undefined,
          description: d.description || '', owner_name: d.owner_name || '', owner_phone: d.owner_phone || '',
          owner_email: d.owner_email || '', features: d.features && typeof d.features === 'object' ? d.features : {},
          authorization_form_id: d.authorization_form_id || undefined,
        });
        setPropertyCategory(categoryOfType(d.property_type) || DEFAULT_CATEGORY);
        // Lock on load, but never overwrite: the listing's saved values are the
        // truth. Autofill only ever runs when the agent picks a form themselves.
        if (d.authorization_form_id) {
          setLinkedForm({ id: d.authorization_form_id, form_ref: d.authorization_form?.form_ref || 'linked form' });
        }
        setImages(Array.isArray(d.images) ? d.images : []);
        setLoaded(true);
      })
      .catch((e) => message.error(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const [customFeat, setCustomFeat] = useState('');
  const toggleFeature = (key) => {
    setF((s) => {
      const next = { ...(s.features || {}) };
      if (next[key]) delete next[key]; else next[key] = true;
      return { ...s, features: next };
    });
    autosave.noteChange();
  };
  const addCustomFeature = () => {
    const key = slugFeature(customFeat);
    if (!key) return;
    setF((s) => ({ ...s, features: { ...(s.features || {}), [key]: true } }));
    autosave.noteChange();
    setCustomFeat('');
  };

  // ── postcode → state / city ────────────────────────────────────────────────
  // Same lookup the admin form uses, so a postcode resolves identically in both.
  // Best-effort: an unknown postcode just leaves the fields for the agent to type.
  const onPostcode = (raw) => {
    const postcode = digitsOnly(raw).slice(0, 5);
    set({ postcode });
    if (postcode.length !== 5) return;
    listingsApi.getListingsPostcodeLookupByPostcode(postcode)
      .then((r) => {
        const loc = r?.data ?? r;
        if (loc?.state) set({ state: loc.state, area: loc.city || '' });
      })
      .catch(() => { /* unknown postcode — leave state/city to the agent */ });
  };

  // ── linked ALS / ALR ───────────────────────────────────────────────────────
  // Reload the pickable forms whenever the listing type changes: an ALS backs a
  // subsale, an ALR backs a rental, and the server refuses a mismatch.
  useEffect(() => {
    if (!loaded) return;
    let cancelled = false;
    listingsApi.listListingAuthorizationForms({ listing_type: f.listing_type, listing_id: id || undefined })
      .then((r) => { if (!cancelled) setAuthForms((r?.data ?? r) || []); })
      .catch(() => { if (!cancelled) setAuthForms([]); });
    return () => { cancelled = true; };
  }, [f.listing_type, loaded, id]);

  const pickAuthForm = (formId) => {
    if (!formId) {
      setLinkedForm(null);
      set({ authorization_form_id: undefined });
      return;
    }
    const form = authForms.find((x) => x.id === formId);
    if (!form) return;
    setLinkedForm(form);
    // Pull across what the signed document already says, so the two can't disagree.
    const patch = { authorization_form_id: formId };
    if (form.property_address) patch.property_address = form.property_address;
    if (form.owner_name) patch.owner_name = form.owner_name;
    if (form.asking_price_rm != null) patch.asking_price_rm = String(form.asking_price_rm);
    if (form.furnish) patch.furnish = form.furnish;
    set(patch);
  };

  // Switching SUBSALE ⇄ RENT invalidates any linked form — the letter is type-specific.
  const onListingType = (v) => {
    set({ listing_type: v, authorization_form_id: undefined });
    setLinkedForm(null);
  };

  // "Convert to listing" from an APPROVED ALS/ALR. Reaches the same state picking the
  // letter from the dropdown would — type set from its sale_type, link applied, the
  // four values it owns autofilled and locked — but from the letter handed over in
  // router state, before the pickable list has even loaded. The shape mirrors the
  // server's _authFormOption so both entry points fill identically.
  useEffect(() => {
    if (!fromAuthForm?.id) return;
    const isRent = fromAuthForm.sale_type === 'RENT';
    const afd = fromAuthForm.form_data || {};
    const price = isRent ? afd.rental_rm : afd.selling_price_rm;
    const option = {
      id: fromAuthForm.id,
      form_ref: fromAuthForm.form_ref,
      status: 'APPROVED',
      sale_type: fromAuthForm.sale_type,
      property_address: afd.property_address || null,
      owner_name: (isRent ? afd.landlord_1_name : afd.owner_1_name) || null,
      asking_price_rm: price != null && price !== '' ? Number(price) : null,
      furnish: isRent ? afd.furnish || null : null,
    };
    setLinkedForm(option);
    setF((s) => ({
      ...s,
      listing_type: isRent ? 'RENT' : 'SUBSALE',
      authorization_form_id: option.id,
      ...(option.property_address ? { property_address: option.property_address } : {}),
      ...(option.owner_name ? { owner_name: option.owner_name } : {}),
      ...(option.asking_price_rm != null ? { asking_price_rm: String(option.asking_price_rm) } : {}),
      ...(option.furnish ? { furnish: option.furnish } : {}),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── owner-from-contacts picker ─────────────────────────────────────────────
  // Paged: the vendor registry runs past one screen, so the sheet scrolls the next
  // page in instead of stopping dead at the first 20. A failed page is swallowed by
  // the hook — this lookup is best-effort, since the manual owner fields below still
  // let the agent type the details in by hand.
  const ownerLookup = usePagedOptions(async (q, page) => {
    const r = await contactsApi.listContactsLookup({ type: 'VENDOR', q, page, limit: CONTACT_PAGE });
    const rows = r?.data ?? r ?? [];
    return (Array.isArray(rows) ? rows : []).map((c) => ({
      value: c.id,
      label: c.name,
      // The lookup returns the registry's own column name (contact_no); older shapes
      // and the contact detail call it `phone`.
      sub: [c.registration_no, c.contact_no || c.phone].filter(Boolean).join(' · ') || undefined,
      data: c,
    }));
  }, CONTACT_PAGE);
  const pickOwner = (c) => {
    setF((s) => ({ ...s, owner_name: c.name || s.owner_name, owner_phone: c.contact_no || c.phone || s.owner_phone, owner_email: c.email || s.owner_email }));
    autosave.noteChange();
    setPickingOwner(false);
  };

  // ── photos: signed-URL upload (upload-url → PUT bytes → confirm), same as detail ──
  // `onProgress(fraction)` reports the bytes of THIS file; the caller folds that
  // into the batch figure so the bar moves within a photo, not just between them.
  const uploadOne = async (listingId, file, onProgress) => {
    // Storage enforces the bucket's size/type rules and answers a violation with a
    // bare 400, so name the problem before spending the upload.
    const problem = checkUpload(file);
    if (problem) throw new Error(problem);

    const ct = file.type || 'application/octet-stream';
    const up = (await listingsApi.createListingsImagesUploadUrlById(listingId, { fileName: file.name, fileSize: file.size, contentType: ct }))?.data;
    if (!up?.uploadUrl) throw new Error('Could not get an upload URL');
    try {
      await axios.put(up.uploadUrl, file, {
        headers: { 'Content-Type': ct, 'x-upsert': 'false' },
        onUploadProgress: (evt) => {
          if (evt.total) onProgress?.(evt.loaded / evt.total);
        },
      });
    } catch {
      throw new Error(`Could not upload “${file.name}”. Please try again.`);
    }
    onProgress?.(1);
    return (await listingsApi.createListingsImagesConfirmUploadById(listingId, { storagePath: up.path }))?.data;
  };

  // Run a list of files through uploadOne, driving the shared progress bar.
  // Sequential so the "n of m" counter means something and a phone isn't holding
  // several photos in flight at once.
  const uploadBatch = async (listingId, files) => {
    const done = [];
    for (let i = 0; i < files.length; i += 1) {
      const label = files.length > 1 ? `Uploading ${i + 1} of ${files.length}` : 'Uploading photo';
      const at = (fraction) => setProgress({ label, percent: Math.round(((i + fraction) / files.length) * 100) });
      at(0);
      done.push(await uploadOne(listingId, files[i], at));
    }
    return done;
  };
  const reloadImages = async () => {
    const r = await listingsApi.getListingsById(id);
    const d = r?.data ?? r;
    setImages(Array.isArray(d?.images) ? d.images : []);
  };
  const onPickFiles = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    if (!picked.length) return;

    setImgBusy(true);
    try {
      // Shrink first: a camera photo is routinely 8–12 MB, which the bucket refuses
      // and which nothing in the app renders at full size anyway. Resizing several
      // of them takes long enough to need its own progress phase.
      const shrunk = await compressAll(picked, {
        onProgress: (done, total) => setProgress({
          label: total > 1 ? `Preparing ${done} of ${total}` : 'Preparing photo',
          percent: Math.round((done / total) * 100),
        }),
      });

      // Then check what survived — a file compression could not decode (HEIC) or
      // could not shrink enough still has to be caught while the agent is looking
      // at the picker. On create these upload only at save, and finding out then
      // would mean a saved listing with photos silently missing.
      const { ok: files, rejected } = partitionUploads(shrunk);
      rejected.forEach((why) => message.warning(why));
      if (!files.length) return;

      if (editing) {
        await uploadBatch(id, files);
        await reloadImages();
        message.success(files.length > 1 ? 'Photos added' : 'Photo added');
      } else {
        const add = files.map((file) => ({ key: `${Date.now()}-${Math.random().toString(36).slice(2)}`, file, url: URL.createObjectURL(file) }));
        setStaged((xs) => [...xs, ...add]);
        setCoverKey((k) => k || add[0].key);
        // Picked photos are unsaved work the leave guard should know about, though
        // the device copy cannot hold the files themselves.
        autosave.noteChange();
      }
    } catch (err) {
      message.error(err.message || 'Upload failed');
    } finally {
      setImgBusy(false);
      setProgress(null);
    }
  };
  const setCoverEdit = async (imageId) => {
    const prev = images;
    setImages((xs) => xs.map((x) => ({ ...x, is_cover: x.id === imageId })));
    try { await listingsApi.updateListingsImagesCoverByIdImageId(id, imageId); }
    catch (err) { setImages(prev); message.error(err.message || 'Could not set cover'); }
  };
  // Confirmed, like the detail page's Remove: this photo is already uploaded, so the
  // ✕ deletes it from storage rather than dropping it from a pending selection — and
  // that ✕ is a small target sitting on the thumbnail itself. Staged photos below are
  // a different case: removeStaged only discards a local pick, so it stays instant.
  const removeImage = (imageId) => {
    modal.confirm({
      title: 'Remove this photo?',
      content: 'It is deleted from the listing and from storage. This cannot be undone — you would have to upload it again.',
      okText: 'Remove photo',
      okButtonProps: { danger: true },
      cancelText: 'Keep it',
      centered: true,
      onOk: async () => {
        const prev = images;
        setImages((xs) => xs.filter((x) => x.id !== imageId));
        try {
          await listingsApi.deleteListingsImagesByIdImageId(id, imageId);
          await reloadImages();
          message.success('Photo removed');
        } catch (err) {
          setImages(prev);
          message.error(err.message || 'Could not remove photo');
          throw err; // keep the dialog open on a server refusal
        }
      },
    });
  };
  const removeStaged = (key) => {
    setStaged((xs) => { const t = xs.find((s) => s.key === key); if (t) URL.revokeObjectURL(t.url); return xs.filter((s) => s.key !== key); });
    autosave.noteChange();
  };
  // Keep a valid cover selected as staged photos are added / removed.
  useEffect(() => {
    if (editing) return;
    if (staged.length && !staged.some((s) => s.key === coverKey)) setCoverKey(staged[0].key);
    if (!staged.length && coverKey) setCoverKey(null);
  }, [staged, coverKey, editing]);
  // Push staged photos once the listing exists — cover first, so it wins is_cover.
  // This runs inside the save, which is the slowest thing the form does, so it
  // drives the same progress bar rather than leaving the agent on a spinner.
  const uploadStagedAfterCreate = async (listingId) => {
    const ordered = coverKey ? [...staged].sort((a, b) => (a.key === coverKey ? -1 : b.key === coverKey ? 1 : 0)) : staged;
    try {
      const confirmed = await uploadBatch(listingId, ordered.map((s) => s.file));
      const coverImageId = confirmed[0]?.id || null;
      if (coverImageId) {
        try { await listingsApi.updateListingsImagesCoverByIdImageId(listingId, coverImageId); }
        catch { /* the first upload already defaults to cover */ }
      }
    } finally {
      setProgress(null);
    }
  };

  // Details the category tab hides (bedrooms on Land, …) go up as explicit nulls,
  // so a listing re-filed from Residential does not keep counts the form no
  // longer shows.
  const buildPayload = () => clearHiddenFields({
    listing_type: f.listing_type,
    property_title: f.property_title.trim(),
    property_address: f.property_address.trim(),
    state: f.state.trim() || null,
    area: f.area.trim() || null,
    postcode: f.postcode.trim() || null,
    property_type: f.property_type || null,
    property_tenure: f.property_tenure || null,
    asking_price_rm: f.asking_price_rm === '' ? null : Number(f.asking_price_rm),
    price_negotiable: !!f.price_negotiable,
    bedrooms: f.bedrooms === '' ? null : Number(f.bedrooms),
    bathrooms: f.bathrooms === '' ? null : Number(f.bathrooms),
    car_parks: f.car_parks === '' ? null : Number(f.car_parks),
    built_up_sqft: f.built_up_sqft === '' ? null : Number(f.built_up_sqft),
    land_area_sqft: f.land_area_sqft === '' ? null : Number(f.land_area_sqft),
    furnish: f.furnish || null,
    floor_level: f.floor_level.trim() || null,
    facing: f.facing || null,
    description: f.description.trim() || null,
    features: f.features || {},
    owner_name: f.owner_name.trim() || null,
    owner_phone: f.owner_phone.trim() || null,
    owner_email: f.owner_email.trim() || null,
    authorization_form_id: f.authorization_form_id || null,
  }, propertyCategory);

  // Field names here match the labels on screen, so the warning points at the
  // starred field the agent is looking at. Same set HQ's validator enforces.
  // Returns what is wrong, or null — Save says it, autosave quietly waits on it.
  const problem = () => {
    if (!f.property_title.trim()) return 'Title is required';
    if (!f.property_address.trim()) return 'Property address is required';
    if (!f.postcode.trim()) return 'Postcode is required';
    // Length and email format are enforced by the server too — checking here just
    // means the agent hears about it without a round trip.
    if (f.postcode.trim().length !== 5) return 'Postcode must be 5 digits';
    if (!f.area.trim()) return 'City / area is required';
    if (!f.state.trim()) return 'State is required';
    if (f.owner_email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.owner_email.trim())) {
      return 'Owner email doesn’t look like a valid address';
    }
    if (f.asking_price_rm === '' || !(Number(f.asking_price_rm) > 0)) {
      return f.listing_type === 'RENT' ? 'Monthly rental is required' : 'Asking price is required';
    }
    return null;
  };
  const validate = () => {
    const why = problem();
    if (why) message.warning(why);
    return !why;
  };

  // Autosave, the owner's "C" (2026-10-02): a new listing is kept on this device
  // until it is saved; a saved DRAFT saves itself to the server, through the same
  // update Save changes makes — never the status call, so autosave cannot publish.
  // A published listing gets the device copy only: a half-typed edit written to
  // the server would be live on the listing. Same keys as the desktop ListingForm,
  // so rotating an iPad across the 1100px switch brings the typing across.
  // Staged photos are files and stay out of the device copy.
  const [loadedTick, setLoadedTick] = useState(0);
  useEffect(() => { if (loaded) setLoadedTick((t) => t + 1); }, [loaded]);
  const isDraft = editing && listingStatus === 'DRAFT';
  const autosave = useFormAutosave({
    draftKey: `listing:${id || `new${fromAuthForm?.id ? `:from:${fromAuthForm.id}` : ''}`}`,
    userId: agent?.id,
    readValues: () => f,
    readMeta: () => ({ propertyCategory, linkedForm }),
    writeValues: (values, meta) => {
      setF((s) => ({ ...s, ...(values || {}) }));
      if (meta.propertyCategory) setPropertyCategory(meta.propertyCategory);
      setLinkedForm(meta.linkedForm || null);
    },
    serverSave: isDraft && loaded ? () => listingsApi.updateListingsById(id, buildPayload()) : undefined,
    canServerSave: () => !problem(),
    serverUpdatedAt,
    loadedTick,
  });

  /** Save without leaving the page. Returns the saved listing, or null (having said why). Throws on an API error. */
  const saveQuietly = async (publish = false) => {
    if (!validate()) return null;
    const token = autosave.changeToken();
    if (editing) {
      await listingsApi.updateListingsById(id, buildPayload());
      autosave.markSaved(token);
      return { id };
    }
    // A listing is always created as a private draft. `publish` decides whether it
    // goes live in the same breath, which is the common case — the agent filling
    // this form in has the property in front of them and nothing to wait for.
    const r = await listingsApi.createListings(buildPayload());
    const d = r?.data ?? r;
    autosave.markSaved(token);
    if (d?.id && staged.length) {
      try { await uploadStagedAfterCreate(d.id); }
      catch { message.warning('Listing saved, but some photos didn’t upload — add them from the listing page.'); }
    }
    if (publish) await listingsApi.updateListingsStatusById(d.id, { status: 'ACTIVE' });
    return d || {};
  };

  const guard = useUnsavedChanges(autosave.dirty, {
    what: 'this listing',
    keptOnDevice: !autosave.toServer,
    // Never publishes: leaving through the guard saves a new listing as a draft.
    onSave: async () => {
      try {
        return !!(await saveQuietly(false));
      } catch (e) {
        message.error(e.message);
        return false;
      }
    },
    saveLabel: editing ? 'Save changes & leave' : 'Save draft & leave',
    onDiscard: autosave.discardDevice,
  });

  const saveEdit = async () => {
    if (!(await saveQuietly())) return;
    message.success('Listing updated');
    guard.release();
    nav(`/listings/${id}`);
  };
  const create = async (publish) => {
    const d = await saveQuietly(publish);
    if (!d) return;
    message.success(publish ? 'Listing published' : 'Draft listing saved');
    guard.release();
    nav(d.id ? `/listings/${d.id}` : '/listings');
  };

  // Feature keys the user added that aren't in the preset grid → shown as removable chips.
  const customKeys = Object.keys(f.features || {}).filter((k) => f.features[k] && !PRESET_KEYS.has(k));

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <AppBar back title={editing ? 'Edit listing' : 'New listing'} sub={editing ? 'changes save to your listing' : 'saves as a private draft'} />

      <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
        {!loaded ? (
          <div style={{ color: 'var(--muted)', fontSize: 13 }}>Loading…</div>
        ) : (
          <>
            <RestoredDraftBanner
              autosave={autosave}
              onServer={editing}
              notYet="It is not saved yet — Save draft keeps it. Photos are not kept on the device; pick them again."
            />
            {/* Sections mirror the admin ListingFormPage card-for-card (Basic /
                Property Details / Location / Pricing / Features / Owner) so the
                same listing reads the same way in both apps. Photos are the one
                extra: the admin form can only add them after saving. */}

            <div style={sectionLabel}>BASIC INFORMATION</div>
            <div style={card}>
              <Field req label="Listing type">
                <Segmented block value={f.listing_type} onChange={onListingType} options={['SUBSALE', 'RENT']} />
              </Field>

              <Field
                tour="listing-auth-link"
                label={f.listing_type === 'RENT' ? 'Linked ALR / EL form' : 'Linked ALS / ES form'}
                hint={locked
                  ? undefined
                  : `Optional. Your issued ${f.listing_type === 'RENT' ? 'rental' : 'sales'} authorisation letters, general and exclusive.`}
              >
                <SheetSelect
                  title={f.listing_type === 'RENT' ? 'Link an ALR or EL form' : 'Link an ALS or ES form'}
                  placeholder="Not linked to an authorisation letter"
                  value={f.authorization_form_id || undefined}
                  selectedLabel={linkedForm?.form_ref}
                  onChange={pickAuthForm}
                  allowClear
                  options={authForms.map((a) => ({
                    value: a.id,
                    label: a.form_ref,
                    // An exclusive letter is called out by name: ES-2026… and
                    // ALS-2026… differ by two characters, and they commit the
                    // owner to very different things.
                    sub: [a.is_exclusive ? 'Exclusive' : null, a.owner_name, a.property_address].filter(Boolean).join(' · ') || undefined,
                  }))}
                  notFoundContent="No issued authorisation letters of yours are free to link."
                />
              </Field>
              {locked && (
                <div data-tour="listing-locked-banner" style={linkBanner}>
                  <b>Linked to {linkedForm.form_ref}.</b>{' '}
                  {f.listing_type === 'RENT'
                    ? 'Address, owner, rental and furnishing come from that letter — clear the link above to edit them here.'
                    : 'Address, owner and price come from that letter — clear the link above to edit them here.'}
                </div>
              )}

              <Field req label="Title">
                <Input data-tour="listing-title" value={f.property_title} onChange={(e) => set({ property_title: e.target.value })} placeholder="3-sty Terrace, Alam Damai" />
              </Field>
              <Field req label="Property address" hint={lockNote}>
                <Input.TextArea data-tour="listing-address" value={f.property_address} onChange={(e) => set({ property_address: e.target.value })} rows={2} placeholder="Full property address" disabled={locked} />
              </Field>
              <Field label="Description" hint="Shown to buyers on the listing page.">
                <Input.TextArea value={f.description} onChange={(e) => set({ description: e.target.value })} rows={3} placeholder="Highlights buyers should know" maxLength={2000} showCount />
              </Field>
            </div>

            <div style={sectionLabel}>PROPERTY DETAILS</div>
            <div style={card}>
              {/* The category narrows the type list and drops the details that do
                  not apply — a plot of land is not asked for bedrooms or a floor
                  level. Chips rather than a Segmented: four labels the length of
                  "Residential" clip inside a 390px segmented control. */}
              <Field label="Property category">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {CATEGORY_OPTIONS.map((c) => {
                    const on = propertyCategory === c.value;
                    return (
                      <span key={c.value} onClick={() => onCategory(c.value)} style={{ cursor: 'pointer', opacity: on ? 1 : 0.55 }}>
                        <Chip label={`${on ? '✓ ' : ''}${c.label}`} tone={on ? 'brand' : undefined} />
                      </span>
                    );
                  })}
                </div>
              </Field>
              <div style={grid2}>
                <Field label="Property type"><SheetSelect title="Property type" value={f.property_type} onChange={(v) => set({ property_type: v })} options={typeOptions(propertyCategory, f.property_type)} placeholder="Select" allowClear /></Field>
                <Field label="Tenure"><SheetSelect title="Tenure" value={f.property_tenure} onChange={(v) => set({ property_tenure: v })} options={TENURES} placeholder="Select" allowClear /></Field>
              </div>
              {fitRow([
                showsField(propertyCategory, 'bedrooms') && <Field key="bedrooms" label="Beds"><Input value={f.bedrooms} onChange={(e) => set({ bedrooms: digitsOnly(e.target.value).slice(0, 2) })} inputMode="numeric" placeholder="3" /></Field>,
                showsField(propertyCategory, 'bathrooms') && <Field key="bathrooms" label="Baths"><Input value={f.bathrooms} onChange={(e) => set({ bathrooms: digitsOnly(e.target.value).slice(0, 2) })} inputMode="numeric" placeholder="2" /></Field>,
                showsField(propertyCategory, 'car_parks') && <Field key="car_parks" label="Car parks"><Input value={f.car_parks} onChange={(e) => set({ car_parks: digitsOnly(e.target.value).slice(0, 2) })} inputMode="numeric" placeholder="2" /></Field>,
              ])}
              {fitRow([
                showsField(propertyCategory, 'built_up_sqft') && <Field key="built_up_sqft" label="Built-up (sqft)"><Input value={groupInt(f.built_up_sqft)} onChange={(e) => set({ built_up_sqft: digitsOnly(e.target.value) })} inputMode="numeric" placeholder="1,450" /></Field>,
                // Land is quoted in acres; the stored figure stays sqft.
                <Field key="land_area_sqft" label="Land area (sqft)" hint={propertyCategory === 'LAND' ? landAreaWords(f.land_area_sqft) : undefined}>
                  <Input value={groupInt(f.land_area_sqft)} onChange={(e) => set({ land_area_sqft: digitsOnly(e.target.value) })} inputMode="numeric" placeholder={propertyCategory === 'LAND' ? '43,560' : '1,600'} />
                </Field>,
              ])}
              {fitRow([
                showsField(propertyCategory, 'furnish') && (
                  <Field key="furnish" label="Furnishing" hint={f.listing_type === 'RENT' ? lockNote : undefined}>
                    <SheetSelect title="Furnishing" value={f.furnish} onChange={(v) => set({ furnish: v })} options={FURNISH} placeholder="Select" allowClear disabled={locked && f.listing_type === 'RENT'} />
                  </Field>
                ),
                showsField(propertyCategory, 'facing') && <Field key="facing" label="Facing"><SheetSelect title="Facing" value={f.facing} onChange={(v) => set({ facing: v })} options={FACING} placeholder="Select" allowClear /></Field>,
              ])}
              {showsField(propertyCategory, 'floor_level') && (
                <Field label="Floor level"><Input value={f.floor_level} onChange={(e) => set({ floor_level: e.target.value })} placeholder="e.g. 12th floor / Ground" /></Field>
              )}
            </div>

            <div style={sectionLabel}>LOCATION</div>
            <div style={card}>
              <div style={grid2}>
                <Field req label="Postcode" hint="Fills state and city."><Input data-tour="listing-postcode" value={f.postcode} onChange={(e) => onPostcode(e.target.value)} inputMode="numeric" maxLength={5} placeholder="56000" /></Field>
                <Field req label="City / area"><Input value={f.area} onChange={(e) => set({ area: e.target.value })} placeholder="Cheras" /></Field>
              </div>
              <Field req label="State">
                <SheetSelect title="State" value={f.state || undefined} onChange={(v) => set({ state: v ?? '' })} options={STATES}
                  placeholder="Select state" showSearch searchPlaceholder="Search state…" allowClear />
              </Field>
            </div>

            <div style={sectionLabel}>PRICING</div>
            <div style={card}>
              <Field req label={f.listing_type === 'RENT' ? 'Monthly rental' : 'Asking price'} hint={lockNote}>
                <MoneyInput value={f.asking_price_rm} onChange={(v) => set({ asking_price_rm: v })} placeholder="980,000" disabled={locked} />
              </Field>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 13, color: 'var(--muted)' }}>Price negotiable</span>
                <Switch checked={f.price_negotiable} onChange={(v) => set({ price_negotiable: v })} />
              </div>
            </div>

            <div style={sectionLabel}>FEATURES</div>
            <div style={card}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {PRESET_FEATURES.map((ft) => {
                  const on = !!f.features?.[ft.key];
                  return (
                    <span key={ft.key} onClick={() => toggleFeature(ft.key)} style={{ cursor: 'pointer', opacity: on ? 1 : 0.55 }}>
                      <Chip label={`${on ? '✓ ' : ''}${ft.label}`} tone={on ? 'brand' : undefined} />
                    </span>
                  );
                })}
              </div>
              {customKeys.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {customKeys.map((k) => (
                    <span key={k} onClick={() => toggleFeature(k)} style={{ cursor: 'pointer' }}>
                      <Chip label={`${k.replace(/_/g, ' ')} ✕`} tone="brand" />
                    </span>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', gap: 10 }}>
                <Input value={customFeat} onChange={(e) => setCustomFeat(e.target.value)} onPressEnter={addCustomFeature} placeholder="Add a custom feature…" />
                <span onClick={addCustomFeature} style={{ cursor: 'pointer', color: 'var(--primary)', fontSize: 13, fontWeight: 600, alignSelf: 'center', whiteSpace: 'nowrap', padding: '0 4px' }}>Add</span>
              </div>
            </div>

            <div style={sectionLabel}>OWNER</div>
            <div style={card}>
              <Field label="Owner (from contacts)" hint={locked ? lockNote : 'Optional — picking one fills the name and phone below.'}>
                {(locked || (!pickingOwner && f.owner_name.trim())) ? (
                  <div style={ownerBox}>
                    <span style={{ fontSize: 13.5, fontWeight: 500, color: locked ? 'var(--muted)' : undefined }}>{f.owner_name || '—'}</span>
                    {!locked && (
                      <span onClick={() => setPickingOwner(true)} style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--primary)', cursor: 'pointer' }}>Change</span>
                    )}
                  </div>
                ) : (
                  <SheetSelect
                    title="Pick an owner from contacts"
                    placeholder="Search your contacts to fill owner…"
                    searchPlaceholder="Search vendors / landlords…"
                    value={null}
                    onSearch={ownerLookup.search}
                    onChange={(cid) => { const c = ownerLookup.options.find((o) => o.value === cid)?.data; if (c) pickOwner(c); }}
                    options={ownerLookup.options}
                    onLoadMore={ownerLookup.loadMore}
                    hasMore={ownerLookup.hasMore}
                    loadingMore={ownerLookup.loadingMore}
                    notFoundContent="No matching contact. Close this and type the owner's details in below."
                  />
                )}
              </Field>
              <Field label="Owner name" hint={lockNote}><Input value={f.owner_name} onChange={(e) => set({ owner_name: e.target.value })} placeholder="Tan Mei Ling" disabled={locked} /></Field>
              <div style={grid2}>
                <Field label="Owner phone"><Input value={f.owner_phone} onChange={(e) => set({ owner_phone: e.target.value })} placeholder="012-345 6789" /></Field>
                <Field label="Owner email"><Input value={f.owner_email} onChange={(e) => set({ owner_email: e.target.value })} placeholder="owner@email.com" /></Field>
              </div>
            </div>

            <div style={sectionLabel}>PHOTOS</div>
            <div style={card}>
              <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: -2 }}>
                Tap a photo to make it the cover. JPG, PNG or WebP, up to {UPLOAD_RULES['listing-images'].maxBytes / (1024 * 1024)} MB each.
                {!editing && staged.length > 0 && ' They upload when you save.'}
              </div>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={onPickFiles} style={{ display: 'none' }} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                {editing
                  ? images.map((im) => (
                      <div key={im.id} onClick={() => { if (!im.is_cover) setCoverEdit(im.id); }} style={photoTile(im.is_cover)}>
                        {im.image_url ? <img src={im.image_url} alt="" style={photoImg} /> : <span style={photoHint}>photo</span>}
                        {im.is_cover && <span style={coverBadge}>COVER</span>}
                        <span onClick={(e) => { e.stopPropagation(); removeImage(im.id); }} style={removeDot}>✕</span>
                      </div>
                    ))
                  : staged.map((s) => {
                      const cover = s.key === coverKey;
                      return (
                        <div key={s.key} onClick={() => setCoverKey(s.key)} style={photoTile(cover)}>
                          <img src={s.url} alt="" style={photoImg} />
                          {cover && <span style={coverBadge}>COVER</span>}
                          <span onClick={(e) => { e.stopPropagation(); removeStaged(s.key); }} style={removeDot}>✕</span>
                        </div>
                      );
                    })}
                <div onClick={() => { if (!imgBusy) fileRef.current?.click(); }} style={addTile}>
                  <span style={{ fontSize: 20, lineHeight: 1 }}>{imgBusy ? '⋯' : '＋'}</span>
                  <span style={{ fontSize: 10.5, fontWeight: 600 }}>{imgBusy ? 'working…' : 'camera / gallery'}</span>
                </div>
              </div>
              <UploadProgress progress={progress} />
            </div>
          </>
        )}
      </div>

      <div style={{ padding: '10px 12px max(12px, env(safe-area-inset-bottom))', borderTop: '1px solid var(--border)', background: 'var(--surface)', flex: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {loaded && (
          <AutosaveStatus
            autosave={autosave}
            idleText={!editing ? 'Save draft keeps it private — Publish puts it live'
              : isDraft ? 'A draft stays private until you publish it'
                : 'Saved changes go live on the listing straight away'}
            savedLabel={`Saved to draft ${listingRef}`}
            waitingText="Fill in the * fields to start saving automatically"
            style={{ textAlign: 'center' }}
          />
        )}
        <div style={{ display: 'flex', gap: 10 }}>
          {editing ? (
            <AsyncButton type="primary" block onClick={saveEdit} onError={(e) => message.error(e.message)}>Save changes</AsyncButton>
          ) : (
            <>
              <AsyncButton block onClick={() => create(false)} onError={(e) => message.error(e.message)}>Save draft</AsyncButton>
              <AsyncButton data-tour="listing-publish" type="primary" block onClick={() => create(true)} onError={(e) => message.error(e.message)}>Publish</AsyncButton>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 };
// A row of whichever detail fields the property category left visible: three
// counts for a house, two for a shop, none for land. Nothing rendered when the
// category hid them all, so the card does not carry an empty gap.
const fitRow = (fields) => {
  const kids = fields.filter(Boolean);
  if (!kids.length) return null;
  return <div style={{ display: 'grid', gridTemplateColumns: `repeat(${kids.length}, 1fr)`, gap: 10 }}>{kids}</div>;
};
// Section eyebrow + card, identical to SaleCreatePage — a listing and a sale
// should not look like they came from two different apps.
const sectionLabel = { fontSize: 11.5, fontWeight: 600, letterSpacing: '.08em', color: 'var(--muted)', marginTop: 2 };
const card = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 16, display: 'flex', flexDirection: 'column', gap: 14, marginTop: -4 };
const ownerBox = { height: 'var(--field-h)', border: '1px solid var(--border)', borderRadius: 12, background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 14px' };
const linkBanner = {
  background: 'color-mix(in srgb,var(--primary) 8%,var(--surface))',
  border: '1px solid color-mix(in srgb,var(--primary) 25%,var(--border))',
  borderRadius: 10,
  padding: '14px 15px',
  fontSize: 11.5,
  lineHeight: 1.5,
  color: 'var(--muted)',
};
const photoTile = (cover) => ({ aspectRatio: '1', borderRadius: 11, background: 'repeating-linear-gradient(45deg,var(--surface2),var(--surface2) 6px,var(--chip) 6px,var(--chip) 12px)', border: `2px solid ${cover ? 'var(--primary)' : 'var(--border)'}`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, cursor: 'pointer', position: 'relative', overflow: 'hidden' });
const photoImg = { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' };
const photoHint = { fontFamily: 'ui-monospace,monospace', fontSize: 10.5, color: 'var(--muted)' };
const coverBadge = { position: 'absolute', top: 6, left: 6, fontSize: 10.5, fontWeight: 700, background: 'var(--primary)', color: '#fff', borderRadius: 999, padding: '2px 7px', letterSpacing: '.04em', zIndex: 1 };
const removeDot = { position: 'absolute', top: 5, right: 5, width: 18, height: 18, borderRadius: 999, background: 'rgba(0,0,0,.55)', color: '#fff', fontSize: 11.5, lineHeight: '18px', textAlign: 'center', zIndex: 1 };
const addTile = { aspectRatio: '1', borderRadius: 11, border: '1.5px dashed var(--border)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, cursor: 'pointer', color: 'var(--muted)' };
// `req` marks a field HQ's validator will reject the listing without, so the agent
// sees it before submitting rather than in an error message afterwards. `hint` is
// the explanatory line under a field. Same shape as SaleCreatePage's Field.
// Batch progress for photo work. Named "n of m" rather than a bare percentage
// because with several large photos the agent's real question is how many are
// left, and the bar alone can look stalled while one big file uploads.
const UploadProgress = ({ progress }) => {
  if (!progress) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, color: 'var(--muted)' }}>
        <span>{progress.label}</span>
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{progress.percent}%</span>
      </div>
      <div style={{ height: 4, borderRadius: 999, background: 'var(--chip)', overflow: 'hidden' }}>
        <div
          style={{
            width: `${Math.min(100, Math.max(0, progress.percent))}%`,
            height: '100%',
            background: 'var(--primary)',
            borderRadius: 999,
            transition: 'width .2s ease',
          }}
        />
      </div>
    </div>
  );
};

const Field = ({ label, req, hint, children, tour }) => (
  <div data-tour={tour} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
    <label style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--muted)' }}>
      {req && <span style={{ color: 'var(--bad)', marginRight: 3 }}>*</span>}{label}
    </label>
    {children}
    {hint && <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: -2 }}>{hint}</div>}
  </div>
);
